import type { User } from "#/feature/auth/auth-context";

import { useAuth } from "#/feature/auth/auth-context";
import { authErrorMessage, fetchAuthConfig } from "#/feature/auth/auth-api";
import { ApiError, post } from "#/client";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  containerStyles,
  contentStyles,
  titleStyles,
  formStyles,
  fieldStyles,
  labelStyles,
  inputStyles,
  buttonStyles,
  errorStyles,
  footerStyles,
  linkStyles,
} from "./login.css";
import { Input } from "#/components/input/input";
import { Button } from "#/components/button/button";

export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [invitationCode, setInvitationCode] = useState("");
  const [invitationRequired, setInvitationRequired] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLogin) {
      void fetchAuthConfig()
        .then((config) => setInvitationRequired(config.invitation_required))
        .catch(() => setInvitationRequired(false));
    }
  }, [isLogin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const body: Record<string, string> = { username, password };
      if (!isLogin && invitationRequired) {
        body.invitation_code = invitationCode;
      }
      const res = await post<{ user: User; token: string }>(endpoint, body);
      login(res.token, res.user);
      if (isLogin) {
        const origin =
          (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || "/";
        void navigate(origin);
      } else {
        void navigate("/register/totp");
      }
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.response.status === 401) setError("Invalid credentials");
        else if (error.response.status === 409) setError("Username already taken");
        else setError(authErrorMessage(error, "Login failed"));
      } else {
        setError("Network error");
      }
    }
  };

  return (
    <div className={containerStyles}>
      <div className={contentStyles}>
        <h1 className={titleStyles}>{isLogin ? "Login" : "Register"}</h1>
        {error && <div className={errorStyles}>{error}</div>}
        <form onSubmit={handleSubmit} className={formStyles}>
          <div className={fieldStyles}>
            <label className={labelStyles}>Username</label>
            <Input
              className={inputStyles}
              type="text"
              value={username}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className={fieldStyles}>
            <label className={labelStyles}>Password</label>
            <Input
              className={inputStyles}
              type="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              required
            />
          </div>
          {!isLogin && invitationRequired && (
            <div className={fieldStyles}>
              <label className={labelStyles}>Invitation code</label>
              <Input
                className={inputStyles}
                type="text"
                value={invitationCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setInvitationCode(e.target.value)
                }
                required
              />
            </div>
          )}
          <Button type="submit" fullWidth className={buttonStyles}>
            {isLogin ? "Login" : "Register"}
          </Button>
        </form>
        {isLogin && (
          <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
            <Link to="/forgot-password" className={linkStyles}>
              Forgot password?
            </Link>
          </div>
        )}
        <div className={footerStyles}>
          <Button variant="ghost" onClick={() => setIsLogin(!isLogin)} className={linkStyles}>
            {isLogin ? "Need an account? Register" : "Have an account? Login"}
          </Button>
        </div>
      </div>
    </div>
  );
}
